'use client';

import { useMemo } from 'react';

import type { TableColumnsDisplayConfig } from '../../shared/table-columns-display';
import type { TaxOverviewTableColumn } from './tax-overview-table-columns';
import { TableColumnsDisplay } from '../../shared/table-columns-display';
import {
  DEFAULT_TABLE_COLUMNS,
  ensureRequiredColumns,
  getDefaultColumns,
  REQUIRED_COLUMN,
  setDefaultColumns,
} from './tax-overview-table-columns';

type DisplayOption = 'Filing Type' | 'Status' | 'Price' | 'Year';

// Map display options to table column names
const DISPLAY_OPTION_TO_COLUMNS: Record<DisplayOption, TaxOverviewTableColumn[]> = {
  'Filing Type': ['Filing Type'],
  Status: ['Tax Status'],
  Price: ['Tax Price'],
  Year: ['Tax Year'],
};

const ORDERED_DISPLAY_OPTIONS: readonly DisplayOption[] = ['Filing Type', 'Status', 'Price', 'Year'] as const;

// Returns ordered array of columns based on selected display options
function displayOptionsToColumns(displayOptions: readonly DisplayOption[]): TaxOverviewTableColumn[] {
  const orderedOptions = ORDERED_DISPLAY_OPTIONS.filter((option) => displayOptions.includes(option));
  return orderedOptions.flatMap((option) => DISPLAY_OPTION_TO_COLUMNS[option]);
}

function columnsToDisplayOptions(columns: readonly TaxOverviewTableColumn[]): DisplayOption[] {
  const result: DisplayOption[] = [];

  ORDERED_DISPLAY_OPTIONS.forEach((option) => {
    const optionColumns = DISPLAY_OPTION_TO_COLUMNS[option];
    const hasAllColumns = optionColumns.every((col) => columns.includes(col));

    if (hasAllColumns) {
      result.push(option);
    }
  });

  return result;
}

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly selectedColumns: readonly TaxOverviewTableColumn[];
  readonly onColumnsChange: (columns: TaxOverviewTableColumn[]) => void;
  readonly anchorEl: HTMLElement | null;
};

export function TaxColumnsDisplay({ open, onClose, selectedColumns, onColumnsChange, anchorEl }: Props) {
  const config: TableColumnsDisplayConfig<TaxOverviewTableColumn, DisplayOption> = useMemo(
    () => ({
      displayOptions: ORDERED_DISPLAY_OPTIONS,
      displayOptionToColumns: (options) => displayOptionsToColumns(options),
      columnsToDisplayOptions,
      requiredColumns: [REQUIRED_COLUMN],
      maxSelectable: ORDERED_DISPLAY_OPTIONS.length,
      ensureRequiredColumns,
      title: 'Display properties',
      countLabel: 'Properties',
      includeRequiredInCount: false,
      getDefaultColumns,
      getHardcodedDefaults: () => [...DEFAULT_TABLE_COLUMNS],
      setDefaultColumns,
      selectedColumns,
      onColumnsChange,
    }),
    [selectedColumns, onColumnsChange],
  );

  return <TableColumnsDisplay open={open} onClose={onClose} anchorEl={anchorEl} config={config} />;
}
